// Pain-tracking data model (PAIN_TRACKING_PLAN.md Phase 1).
//
// Pain is NOT a new EventType (D-PAIN-1). A pinpoint capture is attached to an
// existing `symptom` / `headache` / `injury` event as an optional `bodyMap`
// block (D-PAIN-3), so pain inherits the shipped symptomFollowUp / persistence /
// stats / clustering / condition machinery instead of forking it.
//
// CYCLE BREAK (D-PAIN-4, Task 1.3): `painSiteKey` here takes the ALREADY-COMPUTED
// base follow-up key as its first argument rather than calling
// `symptomFollowUpKey` itself. That keeps this module free of any import from
// `symptomFollowUp.ts`, so `symptomFollowUp.ts` can import `painSiteKey` from
// here without a circular dependency. Every caller passes
// `symptomFollowUpKey(name)` as `baseKey` (the plan's first suggested fix).
//
// Pure data + helpers — safe to import from both the API (Lambda) and the web SPA.
// React-free / JSX-free.

// ── Views ─────────────────────────────────────────────────────────────────────

/**
 * The nine pain-capture panels (D-PAIN-6/7). SEPARATE from `BODY_VIEWS` in
 * web/src/components/bodymap on purpose — extending `BODY_VIEWS` would break the
 * R3 muscle-map check for all 53 muscles (hazard H1).
 */
export const PAIN_VIEWS = [
  "front",
  "back",
  "side",
  "hand-palmar",
  "hand-dorsal",
  "foot-dorsal",
  "foot-plantar",
  "top-down",
  "face",
] as const;
export type PainView = (typeof PAIN_VIEWS)[number];

/**
 * Coordinate-frame version for a complete pain-map payload.  This is deliberately
 * separate from the Trainer artwork: pain marks and radiation persist raw SVG
 * coordinates, so changing their drawing without a version boundary would move
 * a patient's recorded location.
 *
 * Version 1 is the archived production artwork. Version 2 is reserved for the
 * completed redraw and is not writable/renderable until its registry is added.
 */
export const PAIN_ARTWORK_VERSIONS = [1, 2] as const;
export type PainArtworkVersion = (typeof PAIN_ARTWORK_VERSIONS)[number];
export const LEGACY_PAIN_ARTWORK_VERSION: PainArtworkVersion = 1;
export const PRODUCTION_PAIN_ARTWORK_VERSION: PainArtworkVersion = LEGACY_PAIN_ARTWORK_VERSION;

/** Missing values are legacy v1. Invalid/unknown values return null so readers
 * can fail closed instead of drawing them on whichever artwork happens to be
 * current. */
export function resolvePainArtworkVersion(value: unknown): PainArtworkVersion | null {
  if (value === undefined) return LEGACY_PAIN_ARTWORK_VERSION;
  return typeof value === "number" && (PAIN_ARTWORK_VERSIONS as readonly number[]).includes(value)
    ? value as PainArtworkVersion
    : null;
}

/**
 * Canvas size (SVG user units) of each pain-capture panel. Every panel's viewBox
 * starts at "0 0" (see web/src/components/bodymap "Canvas conventions"), so this
 * width/height pair fully describes the coordinate space a `PainMark`'s x/y must
 * fall inside.
 *
 * This exists so the API can bounds-check a submitted mark (PAIN_TRACKING_PLAN.md
 * Task 4.3) without importing the web-only artwork modules. It MUST stay in sync
 * with the `*_VIEWBOX` constants in web/src/components/bodymap/regions*.ts — the
 * front/back/side box is `geometry.ts`'s 220x520 canvas; the six detail panels
 * use the sizes fixed in the plan's "Canvas conventions" section.
 */
export const PAIN_VIEW_BOUNDS: Record<PainView, { width: number; height: number }> = {
  front: { width: 220, height: 520 },
  back: { width: 220, height: 520 },
  side: { width: 220, height: 520 },
  "hand-palmar": { width: 200, height: 260 },
  "hand-dorsal": { width: 200, height: 260 },
  "foot-dorsal": { width: 160, height: 320 },
  "foot-plantar": { width: 160, height: 320 },
  "top-down": { width: 220, height: 220 },
  face: { width: 200, height: 260 },
};

export const PAIN_DEPTHS = ["surface", "muscle", "deep"] as const;
export type PainDepth = (typeof PAIN_DEPTHS)[number];

export const PAIN_AREA_SIZES = ["point", "palm", "diffuse"] as const;
export type PainAreaSize = (typeof PAIN_AREA_SIZES)[number];

export type PainStructureKind =
  | "muscle"
  | "joint"
  | "bone"
  | "nerve"
  | "dermatome"
  | "visceral"
  | "skin";

// ── Marks ─────────────────────────────────────────────────────────────────────

/** One resolved anatomical structure under a mark. `id` is an AnatomicalMuscle
 *  id OR a PainStructure id (shared/src/painStructures.ts). */
export interface PainStructureRef {
  id: string;
  kind: PainStructureKind;
  side: "left" | "right" | "center";
}

/** One pinpoint in one view. Coords are SVG user units in that view's canvas. */
export interface PainMark {
  view: PainView;
  x: number;
  y: number;
  /** Resolved client-side at tap time (D-PAIN-12) from the tapped path element(s). */
  structures: PainStructureRef[];
}

/**
 * Optional block attached to a symptom / headache / injury payload (D-PAIN-3).
 * Additive and optional — every event captured before the Pain page shipped has
 * this absent and keeps working unchanged.
 */
export interface PainBodyMap {
  /** Optional on legacy records; absent is normalized/read as archived artwork v1. */
  artworkVersion?: PainArtworkVersion;
  /** Stable identity, minted once at first log (mintSiteId). */
  siteId: string;
  /** >= 1 mark; more than 1 means multi-view triangulation (D-PAIN-10). */
  marks: PainMark[];
  radiation?: { view: PainView; points: { x: number; y: number }[] };
  depth?: PainDepth;
  areaSize?: PainAreaSize;
  /** Contextual quality chips (see QUALITY_BY_KIND in painStructures.ts). */
  quality?: string[];
  /** Optional user-supplied site name. */
  label?: string;
  /** D-PAIN-9 link to an `injury` event. */
  injuryEventId?: string;
}

// ── Identity (D-PAIN-4) ───────────────────────────────────────────────────────

/**
 * Follow-up identity for a pain-capable event.
 *
 * INVARIANT: when `bodyMap` is absent (or carries no `siteId`) this returns
 * `baseKey` UNCHANGED, byte for byte. Every event in the store today has no
 * `bodyMap`, so today's grouping is unchanged. Making the site-aware branch
 * unconditional would silently re-group every historical symptom — never do
 * that. `scripts/checkPainIdentity.ts` asserts it.
 *
 * `baseKey` is the caller-supplied `symptomFollowUpKey(name)` result — passed in
 * rather than computed here to break the painMap <-> symptomFollowUp import
 * cycle (see this file's header).
 */
export function painSiteKey(baseKey: string, bodyMap?: PainBodyMap | null): string {
  return bodyMap?.siteId ? baseKey + "|" + bodyMap.siteId : baseKey;
}

/**
 * Crypto-random 10-char id. Minted once per distinct pain location, at first log.
 * `crypto.randomUUID` is available in Node 20 and every target browser.
 */
export function mintSiteId(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 10);
}

/**
 * Two marks in the SAME view within this many SVG user units are treated as the
 * same site when OFFERING reuse at log time (never applied silently — the user
 * confirms).
 */
export const SITE_MATCH_RADIUS = 18;

/**
 * Returns the `siteId` of an existing site whose mark in the SAME view as `mark`
 * lies within `SITE_MATCH_RADIUS` user units of it, or null. Compares squared
 * Euclidean distance (no `Math.sqrt`). Marks in a different view never match,
 * even at identical coords.
 */
export function findNearbySite(
  mark: PainMark,
  existing: { siteId: string; marks: PainMark[]; artworkVersion?: PainArtworkVersion }[],
  artworkVersion: PainArtworkVersion = LEGACY_PAIN_ARTWORK_VERSION,
): string | null {
  const r2 = SITE_MATCH_RADIUS * SITE_MATCH_RADIUS;
  for (const site of existing) {
    // Never suggest reuse by raw distance across independently authored frames.
    if (resolvePainArtworkVersion(site.artworkVersion) !== artworkVersion) continue;
    for (const m of site.marks) {
      if (m.view !== mark.view) continue;
      const dx = m.x - mark.x;
      const dy = m.y - mark.y;
      if (dx * dx + dy * dy <= r2) return site.siteId;
    }
  }
  return null;
}
